import React from "react";
import {
  Button,
  Grid,
  useTheme,
  CircularProgress,
  Backdrop,
  TextField,
} from "@material-ui/core";
import { useStressEntryContext } from "./StressEntryContext";
import { useDispositionsByNameAndEnabled } from "../../../common/services/dispositionServices";
import { ErrorMessage } from "../../../common/ErrorMessage";
import { toast } from "react-toastify";
import { useHistory } from "react-router-dom";
import { processErrorOnMutation } from "../../../../util/errorMessaging";
import { useAuthContext } from "../../../common/AuthContext";
import { ConfirmationDialog } from "../../../common/ConfirmationDialog";
import {
  useStressRunSubmit,
  useSubmitMeasurementResult,
  useSubmitProcedureResult,
  useSubmitSmlFileUpload,
  useSubmitStepResult,
  useSubmitStepResultNote,
} from "../../../testCommunication/common/testMutations";
import { find, every, some, isEmpty } from "lodash";
import { useFetchContext } from "../../../common/FetchContext";
import moment from "moment";
import {
  MEASUREMENT_RESULT_END_TIME_NAME,
  MEASUREMENT_RESULT_PAUSE_TIME_NAME,
  MEASUREMENT_RESULT_RESUME_TIME_NAME,
  MEASUREMENT_RESULT_START_TIME_NAME,
  STEP_RESULT_TEST_END_NAME,
  STEP_RESULT_TEST_PAUSE_NAME,
  STEP_RESULT_TEST_RESUME_NAME,
  STEP_RESULT_TEST_START_NAME,
  STRESSOR_CHECK_IN_MODE,
  STRESSOR_CHECK_OUT_MODE,
  STRESSOR_CHECK_PAUSE_MODE,
  STRESSOR_CHECK_RESUME_MODE,
  STRESSOR_NOT_APPLY_METADATA,
} from "./constants";
import { StressStartConfirmDialog } from "./StressStartConfirmDialog";
import { usePostFile } from "../../../common/services/fileServices";

export const StressorStarter = () => {
  const theme = useTheme();
  const history = useHistory();
  const { state, dispatch } = useStressEntryContext();
  const { dispatch: authDispatch } = useAuthContext();
  const { authAxios } = useFetchContext();
  const { mutateAsync: mutateSubmitMeasurementResult } =
    useSubmitMeasurementResult();
  const { mutateAsync: mutateSubmitStepResult } = useSubmitStepResult();
  const { mutateAsync: mutateSubmitStepResultNote } = useSubmitStepResultNote();
  const [newDialogOpen, setNewDialogOpen] = React.useState(false);
  const [isProcessing, setIsProcessing] = React.useState(false);
  const { mutateAsync: mutateSubmitProcedureResult } =
    useSubmitProcedureResult();
  const { mutateAsync: mutateSubmitSmlFileUpload } = useSubmitSmlFileUpload();
  const { mutateAsync: mutateStressRunSubmit } = useStressRunSubmit();
  const { mutateAsync: mutatePostFile } = usePostFile();
  const [
    confirmationDialogStartStressorOpen,
    setConfirmationDialogStartStressorOpen,
  ] = React.useState(false);
  const [notes, setNotes] = React.useState("");
  const onTextChanged = async (item: any) => {
    setNotes(item.target.value);
  };
  const {
    data: dispositionInProgress,
    isLoading: isLoadingDispositionInProgress,
    isError: isErrorDispositionInProgress,
    error: errorDispositionInProgress,
  } = useDispositionsByNameAndEnabled(
    "In Progress",
    state.checkedUnits?.size !== 0,
  );

  const {
    data: dispositionCompleted,
    isLoading: isLoadingDispositionCompleted,
    isError: isErrorDispositionCompleted,
    error: errorDispositionCompleted,
  } = useDispositionsByNameAndEnabled(
    "Completed",
    state.checkedUnits?.size !== 0,
  );

  if (isLoadingDispositionInProgress || isLoadingDispositionCompleted) {
    return (
      <Backdrop
        open={isLoadingDispositionInProgress || isLoadingDispositionCompleted}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
    );
  }

  if (isErrorDispositionInProgress) {
    return <ErrorMessage error={errorDispositionInProgress} />;
  }

  if (isErrorDispositionCompleted) {
    return <ErrorMessage error={errorDispositionCompleted} />;
  }

  const handleStressorProcessing = async () => {
    try {
      setIsProcessing(true);

      let stepResultIds: number[] = [];

      setConfirmationDialogStartStressorOpen(false);
      const values = [...state.checkedUnits.values()];
      const areMetadataValuesEmpty = some(values, (value) => {
        if (
          value.sibling !== STRESSOR_NOT_APPLY_METADATA &&
          (value.metadata === undefined ||
            value.metadata === null ||
            value.metadata === "")
        ) {
          return true;
        }

        // SML-TTF v12 FailureType metadata validation
        if (
          state.isSMLTestCheck &&
          state.unitVersion?.version === "v12" &&
          state.procedureResultIds?.some(
            (p: any) => p.procedureDefinitionName === "SML-TTF",
          ) &&
          (value.FailureType === undefined ||
            value.FailureType === null ||
            value.FailureType === "")
        ) {
          return true;
        }

        return false;
      });

      if (areMetadataValuesEmpty) {
        toast.error("Please fill the metadata values requested.");
      } else {
        let stepResultValueToFind = "";
        let stepResultValueToFindAction = "";
        let measurementResultValueToFind = "";
        let dispositionToUseForProcedureResult = undefined;
        if (state.mode === STRESSOR_CHECK_IN_MODE) {
          stepResultValueToFind = STEP_RESULT_TEST_START_NAME;
          measurementResultValueToFind = MEASUREMENT_RESULT_START_TIME_NAME;
          dispositionToUseForProcedureResult = dispositionInProgress?.id;
        } else if (state.mode === STRESSOR_CHECK_OUT_MODE) {
          stepResultValueToFind = STEP_RESULT_TEST_END_NAME;
          measurementResultValueToFind = MEASUREMENT_RESULT_END_TIME_NAME;
          dispositionToUseForProcedureResult = dispositionCompleted?.id;
        } else if (state.mode === STRESSOR_CHECK_PAUSE_MODE) {
          stepResultValueToFind = STEP_RESULT_TEST_PAUSE_NAME;
          stepResultValueToFindAction = STEP_RESULT_TEST_RESUME_NAME;
          measurementResultValueToFind = MEASUREMENT_RESULT_PAUSE_TIME_NAME;
          dispositionToUseForProcedureResult = dispositionCompleted?.id;
        } else if (state.mode === STRESSOR_CHECK_RESUME_MODE) {
          stepResultValueToFind = STEP_RESULT_TEST_RESUME_NAME;
          measurementResultValueToFind = MEASUREMENT_RESULT_RESUME_TIME_NAME;
          stepResultValueToFindAction = STEP_RESULT_TEST_PAUSE_NAME;
          dispositionToUseForProcedureResult = dispositionCompleted?.id;
        }

        for (let [key, value] of state.checkedUnits) {
          let testStartStepResult = find(value.step_results, [
            "name",
            STEP_RESULT_TEST_START_NAME,
          ]);
          let testEndStepResult = find(value.step_results, [
            "name",
            STEP_RESULT_TEST_END_NAME,
          ]);
          let testPauseStepResult = find(value.step_results, [
            "name",
            STEP_RESULT_TEST_PAUSE_NAME,
          ]);

          let testResumeStepResult = find(value.step_results, [
            "name",
            STEP_RESULT_TEST_RESUME_NAME,
          ]);
          let testStartMeasurementResult = find(
            testStartStepResult.measurement_results,
            ["name", MEASUREMENT_RESULT_START_TIME_NAME],
          );

          let testEndMeasurementResult = find(
            testEndStepResult.measurement_results,
            ["name", MEASUREMENT_RESULT_END_TIME_NAME],
          );

          let testPauseMeasurementResult = find(
            testPauseStepResult.measurement_results,
            ["name", MEASUREMENT_RESULT_PAUSE_TIME_NAME],
          );

          let testResumeMeasurementResult = find(
            testResumeStepResult.measurement_results,
            ["name", MEASUREMENT_RESULT_RESUME_TIME_NAME],
          );
          // Check for Stress Entry before an Stress Exit Submission
          if (
            state.mode === STRESSOR_CHECK_OUT_MODE &&
            isEmpty(testStartStepResult.disposition) &&
            isEmpty(testStartMeasurementResult.disposition)
          ) {
            toast.error(
              `Stress Entry for procedure ${value.id} doesn't exists.`,
            );
          } else if (
            state.mode === STRESSOR_CHECK_RESUME_MODE &&
            isEmpty(testStartStepResult.disposition) &&
            isEmpty(testStartMeasurementResult.disposition)
          ) {
            toast.error(
              `Stress Entry for procedure ${value.id} doesn't exists.`,
            );
          } else if (
            state.mode === STRESSOR_CHECK_PAUSE_MODE &&
            isEmpty(testStartStepResult.disposition) &&
            isEmpty(testStartMeasurementResult.disposition)
          ) {
            toast.error(
              `Stress Entry for procedure ${value.id} doesn't exists.`,
            );
          } else {
            let procedureResult = value;
            let stepResult =
              state.mode === STRESSOR_CHECK_OUT_MODE
                ? find(procedureResult.step_results, [
                    "name",
                    stepResultValueToFind,
                  ])
                : state.mode === STRESSOR_CHECK_PAUSE_MODE
                  ? testPauseStepResult
                  : state.mode === STRESSOR_CHECK_RESUME_MODE
                    ? testResumeStepResult
                    : testStartStepResult;
            let measurementResult =
              state.mode === STRESSOR_CHECK_OUT_MODE
                ? find(stepResult.measurement_results, [
                    "name",
                    measurementResultValueToFind,
                  ])
                : state.mode === STRESSOR_CHECK_PAUSE_MODE
                  ? testPauseMeasurementResult
                  : state.mode === STRESSOR_CHECK_RESUME_MODE
                    ? testResumeMeasurementResult
                    : testStartMeasurementResult;

            let stepResultAction =
              state.mode === STRESSOR_CHECK_PAUSE_MODE
                ? testResumeStepResult
                : testPauseStepResult;

            if (state.mode === STRESSOR_CHECK_IN_MODE) {
              if (state.isCidTestCheck === true) {
                if (state.Applied_currentCID === null) {
                  toast.error("Please fill all required CID test details.");
                  return;
                }
                await mutateSubmitStepResultNote({
                  authAxios,
                  procedure_result: procedureResult.id,
                  step_result: stepResult.id,
                  step_result_action_id: stepResultAction.id,
                  datetime: state.stressEntryDate,
                  asset_id: state.selectedStressType.id,
                  notes: notes,
                  applied_current: state.Applied_currentCID,
                });
              } else if (state.isHumidityTestCheck === true) {
                if (state.Applied_currentHF === null) {
                  toast.error(
                    "Please fill all required HUMIDITY FREEZE test details.",
                  );
                  return;
                }
                await mutateSubmitStepResultNote({
                  authAxios,
                  procedure_result: procedureResult.id,
                  step_result: stepResult.id,
                  step_result_action_id: stepResultAction.id,
                  datetime: state.stressEntryDate,
                  asset_id: state.selectedStressType.id,
                  notes: notes,
                  applied_current_low: state.Applied_currentHF ?? 0.1,
                });
              } else if (state.isHailTestCheck === true) {
                if (
                  // state.HTAmbient_humidity === null ||
                  // state.HTAmbient_temperature === null ||
                  // state.HTCalibration_date === "" ||
                  state.HTHail_test_specs === null ||
                  state.HTMounting_method === "" ||
                  state.HTMounting_tilt === null
                ) {
                  toast.error(
                    "Please fill all required HAILTEST test details.",
                  );
                  return;
                }
                await mutateSubmitStepResultNote({
                  authAxios,
                  procedure_result: procedureResult.id,
                  step_result: stepResult.id,
                  step_result_action_id: stepResultAction.id,
                  datetime: state.stressEntryDate,
                  asset_id: state.selectedStressType.id,
                  notes: notes,
                  ambient_humidity: state.HTAmbient_humidity,
                  ambient_temperature: state.HTAmbient_temperature,
                  equipment_calibration_date: state.HTCalibration_date,
                  hailtest_specs: state.HTHail_test_specs,
                  mounting_method: state.HTMounting_method,
                  mounting_tilt: state.HTMounting_tilt,
                });
              } else if (state.isLightSoakCheck === true) {
                if (
                  // state.stressCalibrationDate === "" ||
                  // state.irradiance === null ||
                  state.lightExposure === null ||
                  state.lightExposure === "" ||
                  state.loadType === null
                ) {
                  toast.error(
                    "Please fill all required LIGHTSOAK test details.",
                  );
                  return;
                }
                await mutateSubmitStepResultNote({
                  authAxios,
                  procedure_result: procedureResult.id,
                  step_result: stepResult.id,
                  step_result_action_id: stepResultAction.id,
                  datetime: state.stressEntryDate,
                  calibration_date: state.stressCalibrationDate,
                  irradiance: state.irradiance,
                  light_exposure: state.lightExposure,
                  load_type: state.loadType,
                  fixed_resistor_value: state.fixed_resistor_value,
                  asset_id: state.selectedStressType.id,
                  notes: notes,
                });
              } else if (state.isDMLTestCheck === true) {
                if (
                  state.DMLAmbient_temperature === null ||
                  state.DMLAmbient_humidity === null ||
                  state.DMLCalibration_date === "" ||
                  state.DMLInstallation_method === "" ||
                  state.DMLMounting_hardware === ""
                ) {
                  toast.error("Please fill all required DML test details.");
                  return;
                }
                await mutateSubmitStepResultNote({
                  authAxios,
                  procedure_result: procedureResult.id,
                  step_result: stepResult.id,
                  step_result_action_id: stepResultAction.id,
                  datetime: state.stressEntryDate,
                  asset_id: state.selectedStressType.id,
                  notes: notes,
                  ambient_temperature: state.DMLAmbient_temperature,
                  ambient_humidity: state.DMLAmbient_humidity,
                  equipment_calibration_date: state.DMLCalibration_date,
                  installation_method: state.DMLInstallation_method,
                  mounting_hardware: state.DMLMounting_hardware,
                });
              } else if (state.isSMLTestCheck === true) {
                if (
                  // state.SMLAmbient_temperature === null ||
                  // state.SMLAmbient_humidity === null ||
                  // state.SMLCalibration_date === "" ||
                  state.SMLInstallation_method === "" ||
                  state.SMLMounting_hardware === ""
                ) {
                  toast.error("Please fill all required SML test details.");
                  return;
                }
                await mutateSubmitStepResultNote({
                  authAxios,
                  procedure_result: procedureResult.id,
                  step_result: stepResult.id,
                  step_result_action_id: stepResultAction.id,
                  datetime: state.stressEntryDate,
                  asset_id: state.selectedStressType.id,
                  notes: notes,
                  ambient_temperature: state.SMLAmbient_temperature,
                  ambient_humidity: state.SMLAmbient_humidity,
                  equipment_calibration_date: state.SMLCalibration_date,
                  installation_method: state.SMLInstallation_method,
                  mounting_hardware: state.SMLMounting_hardware,
                });
              } else {
                await mutateSubmitStepResultNote({
                  authAxios,
                  procedure_result: procedureResult.id,
                  step_result: stepResult.id,
                  step_result_action_id: stepResultAction.id,
                  datetime: state.stressEntryDate,
                  asset_id: state.selectedStressType.id,
                  notes: notes,
                });
              }
            } else if (state.mode === STRESSOR_CHECK_OUT_MODE) {
              if (state.isDAMPHeatCheck === true) {
                if (
                  // state.DampheatCalibration_date === "" ||
                  state.DampheatTest_temperature === null ||
                  state.DampheatTest_duration === null ||
                  state.DampheatTest_humidity === null
                ) {
                  toast.error(
                    "Please fill all required DAMPHEAT  test details.",
                  );
                  return;
                }
                await mutateSubmitStepResultNote({
                  authAxios,
                  procedure_result: procedureResult.id,
                  step_result: stepResult.id,
                  step_result_action_id: stepResultAction.id,
                  datetime: state.stressEntryDate,
                  asset_id: state.selectedStressType.id,
                  notes: notes,
                  test_duration: state.DampheatTest_duration,
                  test_temperature: state.DampheatTest_temperature,
                  test_humidity: state.DampheatTest_humidity,
                  equipment_calibration_date: state.DampheatCalibration_date,
                  weight: state.DampheatWeights,
                });
              } else if (state.isPidTestCheck === true) {
                if (
                  state.PidCalibration_date === "" ||
                  state.PidTest_duration === null ||
                  state.PidTest_temperature === null ||
                  state.PidTest_humidity === null ||
                  state.PidVoltage_is_normal === null
                ) {
                  toast.error("Please fill all required PID  test details.");
                  return;
                }
                await mutateSubmitStepResultNote({
                  authAxios,
                  procedure_result: procedureResult.id,
                  step_result: stepResult.id,
                  step_result_action_id: stepResultAction.id,
                  datetime: state.stressEntryDate,
                  asset_id: state.selectedStressType.id,
                  notes: notes,
                  test_duration: state.PidTest_duration,
                  test_temperature: state.PidTest_temperature,
                  test_humidity: state.PidTest_humidity,
                  equipment_calibration_date: state.PidCalibration_date,
                  voltage_normal: state.PidVoltage_is_normal,
                });
              } else if (state.isUvidTestCheck === true) {
                if (
                  // state.UVIDCalibration_date === "" ||
                  // state.UVAB_irradiance === null ||
                  // state.Type_of_light_source === "" ||
                  state.Set_temperature === null ||
                  !state.DUT_operating_condition === null ||
                  state.UVAB_total_dose === null ||
                  // state.UVB_irradiance_ratio === null ||
                  !state.DUT_exposed_surface === null ||
                  !state.Temperature_control_mode
                  // state.DUT_average_temperature === null
                ) {
                  toast.error("Please fill all required UVID  test details.");
                  return;
                }
                await mutateSubmitStepResultNote({
                  authAxios,
                  procedure_result: procedureResult.id,
                  step_result: stepResult.id,
                  step_result_action_id: stepResultAction.id,
                  datetime: state.stressEntryDate,
                  asset_id: state.selectedStressType.id,
                  notes: notes,
                  uvab_irradiance: state.UVAB_irradiance,
                  uvab_totaldose: state.UVAB_total_dose,
                  uvab_irradiance_ratio: state.UVB_irradiance_ratio,
                  type_of_lightsource: state.Type_of_light_source,
                  dut_operatingcondition: state.DUT_operating_condition,
                  dut_exposedsurface: state.DUT_exposed_surface,
                  set_temperature: state.Set_temperature,
                  temperature_controlmode: state.Temperature_control_mode,
                  equipment_calibration_date: state.UVIDCalibration_date,
                  dut_averagetemperature: state.DUT_average_temperature,
                });
              } else if (state.isLetidTestCheck === true) {
                if (
                  state.LetidCalibration_date === "" ||
                  state.LetidTest_duration === null ||
                  state.LetidTest_temperature === null
                ) {
                  toast.error("Please fill all required LETID  test details.");
                  return;
                }
                await mutateSubmitStepResultNote({
                  authAxios,
                  procedure_result: procedureResult.id,
                  step_result: stepResult.id,
                  step_result_action_id: stepResultAction.id,
                  datetime: state.stressEntryDate,
                  asset_id: state.selectedStressType.id,
                  notes: notes,
                  test_duration: state.LetidTest_duration,
                  test_temperature: state.LetidTest_temperature,
                  equipment_calibration_date: state.LetidCalibration_date,
                  current_normal: state.LetidCurrent_is_normal,
                });
              } else if (state.isThermalCyclingCheck === true) {
                if (
                  // state.TCCalibration_date === "" ||
                  !state.TCTest_duration === null ||
                  state.TCTest_condition === null ||
                  state.TCNumber_of_TestCycles === null
                ) {
                  toast.error(
                    "Please fill all required Thermal Cycle test details.",
                  );
                  return;
                }
                await mutateSubmitStepResultNote({
                  authAxios,
                  procedure_result: procedureResult.id,
                  step_result: stepResult.id,
                  step_result_action_id: stepResultAction.id,
                  datetime: state.stressEntryDate,
                  asset_id: state.selectedStressType.id,
                  notes: notes,
                  test_duration: state.TCTest_duration,
                  test_condition: state.TCTest_condition,
                  equipment_calibration_date: state.TCCalibration_date,
                  number_of_cycles: state.TCNumber_of_TestCycles,
                  current_voltage_normal: state.TCVoltage_is_normal,
                  weight: state.TCweight,
                });
              } else if (state.isHumidityTestCheck === true) {
                if (
                  state.HFCalibration_date === "" ||
                  state.HFTest_temperature === null ||
                  state.HFTest_humidity === null ||
                  state.HFNumber_of_TestCycles === null
                ) {
                  toast.error(
                    "Please fill all required Humidity Freeze test details.",
                  );
                  return;
                }
                await mutateSubmitStepResultNote({
                  authAxios,
                  procedure_result: procedureResult.id,
                  step_result: stepResult.id,
                  step_result_action_id: stepResultAction.id,
                  datetime: state.stressEntryDate,
                  asset_id: state.selectedStressType.id,
                  notes: notes,
                  test_temperature: state.HFTest_temperature,
                  test_humidity: state.HFTest_humidity,
                  equipment_calibration_date: state.HFCalibration_date,
                  number_of_cycles: state.HFNumber_of_TestCycles,
                  current_voltage_normal: state.HFVoltage_is_normal,
                });
              } else if (state.isCidTestCheck === true) {
                if (
                  state.CidCalibration_date === "" ||
                  state.CidTest_duration === null ||
                  state.CidTest_temperature === null
                ) {
                  toast.error(
                    "Please fill all required Humidity Freeze test details.",
                  );
                  return;
                }
                await mutateSubmitStepResultNote({
                  authAxios,
                  procedure_result: procedureResult.id,
                  step_result: stepResult.id,
                  step_result_action_id: stepResultAction.id,
                  datetime: state.stressEntryDate,
                  asset_id: state.selectedStressType.id,
                  notes: notes,
                  test_duration: state.CidTest_duration,
                  test_temperature: state.CidTest_temperature,
                  equipment_calibration_date: state.CidCalibration_date,
                  current_normal: state.CidCurrent_is_normal,
                });
              } else if (state.isHailTestCheck === true) {
                if (
                  state.unitVersion?.version === "v12" &&
                  state.entries.length === 0 &&
                  state.procedureResultIds?.some(
                    (p: any) =>
                      p.procedureDefinitionName === "Hail Strike Event v12",
                  )
                ) {
                  toast.error("Please Upload Hail Test File");
                  return;
                }
                await mutateSubmitStepResultNote({
                  authAxios,
                  procedure_result: procedureResult.id,
                  step_result: stepResult.id,
                  step_result_action_id: stepResultAction.id,
                  datetime: state.stressEntryDate,
                  asset_id: state.selectedStressType.id,
                  notes: notes,
                  entries: state.entries,
                });
              } else if (state.isSMLTestCheck === true) {
                if (
                  state.unitVersion?.version === "v12" &&
                  state.procedureResultIds?.some(
                    (p: any) => p.procedureDefinitionName === "SML-TTF",
                  ) &&
                  state.Sml_file_id === null
                ) {
                  toast.error("Please Upload SML Test File");
                  return;
                }

                const uploadedFiles = [];

                if (state.smlImages.length > 0) {
                  for (const file of state.smlImages) {
                    const response = await mutatePostFile({
                      authAxios,
                      file: file,
                    });

                    uploadedFiles.push(response?.id);
                  }
                }

                console.log(uploadedFiles);
                await mutateSubmitStepResultNote({
                  authAxios,
                  procedure_result: procedureResult.id,
                  step_result: stepResult.id,
                  step_result_action_id: stepResultAction.id,
                  datetime: state.stressEntryDate,
                  asset_id: state.selectedStressType.id,
                  notes: notes,
                  entries: state.entries,
                });

                if (state.Sml_file_id !== null) {
                  await mutateSubmitSmlFileUpload({
                    authAxios,
                    asset_id: state.selectedStressType.id,
                    azure_file_id: state.Sml_file_id,
                    procedure_result_id: procedureResult.id,
                    type: "upload",
                    sml_images_ids:
                      uploadedFiles && uploadedFiles.length > 0
                        ? uploadedFiles
                        : null,
                  });
                }
              } else if (state.isLightSoakCheck === true) {
                if (!state.averageTemperature === null) {
                  toast.error(
                    "Please fill all required LIGHTSOAK test details.",
                  );
                  return;
                }
                await mutateSubmitStepResultNote({
                  authAxios,
                  procedure_result: procedureResult.id,
                  step_result: stepResult.id,
                  step_result_action_id: stepResultAction.id,
                  datetime: state.stressEntryDate,
                  average_temperature: state.averageTemperature,
                  asset_id: state.selectedStressType.id,
                  notes: notes,
                });
              } else if (state.isDMLTestCheck === true) {
                if (
                  state.DMLTest_pressure === null ||
                  state.DMLNumber_of_test_cycles === null ||
                  state.DMLTotal_Number_of_test_cycles === null
                ) {
                  toast.error("Please fill all required DML test details.");
                  return;
                }
                await mutateSubmitStepResultNote({
                  authAxios,
                  procedure_result: procedureResult.id,
                  step_result: stepResult.id,
                  step_result_action_id: stepResultAction.id,
                  datetime: state.stressEntryDate,
                  asset_id: state.selectedStressType.id,
                  notes: notes,
                  test_pressure: state.DMLTest_pressure,
                  no_of_test_cycle: state.DMLNumber_of_test_cycles,
                  total_test_cycle: state.DMLTotal_Number_of_test_cycles,
                  dut_circuit_normal: state.DMLDUT_circuit_check_normal,
                });
              } else {
                await mutateSubmitStepResultNote({
                  authAxios,
                  procedure_result: procedureResult.id,
                  step_result: stepResult.id,
                  step_result_action_id: stepResultAction.id,
                  datetime: state.stressEntryDate,
                  asset_id: state.selectedStressType.id,
                  notes: notes,
                  // entries: state.entries,
                });
              }
            } else {
              await mutateSubmitStepResultNote({
                authAxios,
                procedure_result: procedureResult.id,
                step_result: stepResult.id,
                step_result_action_id: stepResultAction.id,
                datetime: state.stressEntryDate,
                asset_id: state.selectedStressType.id,
                notes: notes,
              });
            }

            await mutateSubmitMeasurementResult({
              authAxios,
              disposition: dispositionCompleted?.id,
              measurementResultId: measurementResult.id,
              start_datetime: moment().format(),
              result_datetime: state.stressEntryDate,
              asset: state.selectedStressType.id,
            });

            await mutateSubmitStepResult({
              authAxios,
              disposition: dispositionCompleted?.id,
              stepResultId: stepResult.id,
              start_datetime: moment().format(),
            });

            //add stepResult.id to array
            stepResultIds.push(stepResult.id);

            // Metadata process
            if (value.sibling !== STRESSOR_NOT_APPLY_METADATA) {
              const stepResultSibling = value.sibling;
              const measurementResultSibling =
                stepResultSibling.measurement_results[0];
              const resultTypeField =
                measurementResultSibling.measurement_result_type_field;

              const applyCurrentLowStepResultId = value.step_results[1];
              const applyCurrentLowMeasurementResultId =
                value.step_results[1].measurement_results[0];

              await mutateSubmitMeasurementResult({
                authAxios,
                disposition: dispositionCompleted?.id,
                measurementResultId: measurementResultSibling?.id,
                asset: state.selectedStressType.id,
                [resultTypeField]: value.metadata,
              });

              await mutateSubmitStepResult({
                authAxios,
                disposition: dispositionCompleted?.id,
                stepResultId: stepResultSibling.id,
              });

              if (
                state.isSMLTestCheck &&
                state.unitVersion?.version === "v12" &&
                state.procedureResultIds?.some(
                  (p: any) => p.procedureDefinitionName === "SML-TTF",
                )
              ) {
                const SMLresultTypeField =
                  value.step_results[5].measurement_results[0]
                    .measurement_result_type_field;

                const SmlFailStepResultId = value.step_results[5];
                const SMlFailMeasurementResultId =
                  value.step_results[5].measurement_results[0];
                const payload = {
                  authAxios,
                  disposition: dispositionCompleted?.id,
                  measurementResultId: SMlFailMeasurementResultId?.id,
                  asset: state.selectedStressType.id,
                  [SMLresultTypeField]: value.FailureType,
                };

                await mutateSubmitMeasurementResult({
                  authAxios,
                  disposition: dispositionCompleted?.id,
                  measurementResultId: SMlFailMeasurementResultId?.id,
                  asset: state.selectedStressType.id,
                  [SMLresultTypeField]: value.FailureType,
                });
                await mutateSubmitStepResult({
                  authAxios,
                  disposition: dispositionCompleted?.id,
                  stepResultId: SmlFailStepResultId?.id,
                });
              }

              if (state.isThermalCyclingCheck) {
                await mutateSubmitMeasurementResult({
                  authAxios,
                  disposition: dispositionCompleted?.id,
                  measurementResultId: applyCurrentLowMeasurementResultId?.id,
                  asset: state.selectedStressType.id,
                  [resultTypeField]: value.lowCurrent,
                });

                await mutateSubmitStepResult({
                  authAxios,
                  disposition: dispositionCompleted?.id,
                  stepResultId: applyCurrentLowStepResultId.id,
                });
              }
            }

            if (state.mode === STRESSOR_CHECK_IN_MODE) {
              await mutateSubmitProcedureResult({
                authAxios,
                disposition: dispositionToUseForProcedureResult,
                procedureResultId: procedureResult.id,
                start_datetime: state.stressEntryDate,
              });
            } else if (state.mode === STRESSOR_CHECK_OUT_MODE) {
              await mutateSubmitProcedureResult({
                authAxios,
                disposition: dispositionToUseForProcedureResult,
                procedureResultId: procedureResult.id,
                end_datetime: state.stressEntryDate,
              });
            }

            if (state.mode === STRESSOR_CHECK_IN_MODE) {
              toast.success(`Serial Number ${key} was successfully checked in`);
            } else if (state.mode === STRESSOR_CHECK_OUT_MODE) {
              toast.success(
                `Serial Number ${key} was successfully checked out`,
              );
            } else if (state.mode === STRESSOR_CHECK_RESUME_MODE) {
              toast.success(`Serial Number ${key} was successfully resumed`);
            } else if (state.mode === STRESSOR_CHECK_PAUSE_MODE) {
              toast.success(`Serial Number ${key} was successfully paused `);
            }
          }
          if (state.mode === STRESSOR_CHECK_IN_MODE) {
            toast.success("Check-in process completed");
          } else if (state.mode === STRESSOR_CHECK_OUT_MODE) {
            toast.success("Check-out process completed");
          } else {
            toast.success("Process completed");
          }
          dispatch({ type: "CLEAN_STRESS_ENTRY" });
        }

        if (state.mode === STRESSOR_CHECK_IN_MODE) {
          if (state.selectedFiles?.length > 0) {
            const azure_file_ids: number[] = [];

            for (const file of state.selectedFiles) {
              const postedFile = await mutatePostFile({
                authAxios,
                file,
              });

              azure_file_ids.push(postedFile.id);
            }

            await mutateStressRunSubmit({
              authAxios,
              run_name:
                (state.selectedStressType?.name ?? "") +
                "_" +
                (state.selectedStressProcedure?.name ?? "") +
                "_" +
                state.stressEntryDate.replace(" ", "_"),
              asset_id: state.selectedStressType.id,
              step_result_ids: stepResultIds,
              comment: "stress_start",
              sub_asset_ids: state.selectedStressLinkedAssets.map(
                (asset: any) => asset.sub_asset_id,
              ),
              azure_file_id: azure_file_ids,
            });
          } else {
            await mutateStressRunSubmit({
              authAxios,
              run_name:
                (state.selectedStressType?.name ?? "") +
                "_" +
                (state.selectedStressProcedure?.name ?? "") +
                "_" +
                state.stressEntryDate.replace(" ", "_"),
              asset_id: state.selectedStressType.id,
              step_result_ids: stepResultIds,
              comment: "stress_start",
              sub_asset_ids: state.selectedStressLinkedAssets.map(
                (asset: any) => asset.sub_asset_id,
              ),
            });
          }
        } else if (state.mode === STRESSOR_CHECK_RESUME_MODE) {
          if (state.selectedFiles?.length > 0) {
            const azure_file_ids: number[] = [];

            for (const file of state.selectedFiles) {
              const postedFile = await mutatePostFile({
                authAxios,
                file,
              });

              azure_file_ids.push(postedFile.id);
            }

            await mutateStressRunSubmit({
              authAxios,
              run_name:
                (state.selectedStressType?.name ?? "") +
                "_" +
                (state.selectedStressProcedure?.name ?? "") +
                "_" +
                state.stressEntryDate.replace(" ", "_"),
              asset_id: state.selectedStressType.id,
              step_result_ids: stepResultIds,
              comment: "stress_resume",
              sub_asset_ids: state.selectedStressLinkedAssets.map(
                (asset: any) => asset.sub_asset_id,
              ),
              azure_file_id: azure_file_ids,
            });
          } else {
            await mutateStressRunSubmit({
              authAxios,
              run_name:
                (state.selectedStressType?.name ?? "") +
                "_" +
                (state.selectedStressProcedure?.name ?? "") +
                "_" +
                state.stressEntryDate.replace(" ", "_"),
              asset_id: state.selectedStressType.id,
              step_result_ids: stepResultIds,
              comment: "stress_resume",
              sub_asset_ids: state.selectedStressLinkedAssets.map(
                (asset: any) => asset.sub_asset_id,
              ),
            });
          }
        } else if (
          state.mode === STRESSOR_CHECK_PAUSE_MODE ||
          state.mode === STRESSOR_CHECK_OUT_MODE
        ) {
          if (state.selectedFiles?.length > 0) {
            const log_azure_file_ids: number[] = [];

            for (const file of state.selectedFiles) {
              const postedFile = await mutatePostFile({
                authAxios,
                file,
              });

              log_azure_file_ids.push(postedFile.id);
            }

            // Files present
            await mutateStressRunSubmit({
              authAxios,
              asset_id: state.selectedStressType.id,
              step_result_ids: stepResultIds,
              log_azure_file_id: log_azure_file_ids,
            });
          } else {
            // File not present
            await mutateStressRunSubmit({
              authAxios,
              asset_id: state.selectedStressType.id,
              step_result_ids: stepResultIds,
            });
          }
        } else {
          await mutateStressRunSubmit({
            authAxios,
            asset_id: state.selectedStressType.id,
            step_result_ids: stepResultIds,
          });
        }
      }
      setIsProcessing(false);
    } catch (err) {
      if (state.mode === STRESSOR_CHECK_IN_MODE) {
        toast.error("Error while executing check-in routine.");
      } else if (state.mode === STRESSOR_CHECK_OUT_MODE) {
        toast.error("Error while executing check-out routine.");
      } else {
        toast.error("Error while executing routine.");
      }
      setIsProcessing(false);
      processErrorOnMutation(err, authDispatch, history);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleReset = (event: React.MouseEvent<HTMLButtonElement>) => {
    dispatch({ type: "CLEAN_STRESS_ENTRY" });
  };

  return (
    <Grid container direction="row" justifyContent="flex-start" spacing={2}>
      <Grid item xs={4} style={{ textAlign: "right" }}>
        {state.selectedStressType && (
          <Button color="secondary" variant="contained" onClick={handleReset}>
            Reset
          </Button>
        )}
      </Grid>
      <Grid item xs={6} style={{ textAlign: "right" }}>
        {state.checkedUnits.size !== 0 && (
          <Button
            style={{ backgroundColor: "#85DA47", color: "#000" }}
            variant="contained"
            disabled={isProcessing}
            onClick={() => {
              if (
                state.mode === STRESSOR_CHECK_IN_MODE ||
                state.mode === STRESSOR_CHECK_RESUME_MODE
              ) {
                setNewDialogOpen(true);
              } else {
                setConfirmationDialogStartStressorOpen(true);
              }
            }}
          >
            {isProcessing ? (
              <CircularProgress size={24} style={{ color: "#000" }} />
            ) : state.mode === STRESSOR_CHECK_IN_MODE ? (
              "Start Stressor"
            ) : state.mode === STRESSOR_CHECK_OUT_MODE ? (
              "End Stressor"
            ) : state.mode === STRESSOR_CHECK_PAUSE_MODE ? (
              "Pause Stressor"
            ) : (
              "Resume Stressor"
            )}
          </Button>
        )}
      </Grid>

      <Grid item xs={12}>
        {state.checkedUnits.size !== 0 &&
          (state.mode === STRESSOR_CHECK_PAUSE_MODE ||
            state.mode === STRESSOR_CHECK_RESUME_MODE) && (
            <TextField
              type="text"
              name="comment"
              fullWidth
              label="Note"
              onChange={(value) => onTextChanged(value)}
              variant="outlined"
            ></TextField>
          )}
      </Grid>

      {(state.mode === STRESSOR_CHECK_IN_MODE ||
        state.mode === STRESSOR_CHECK_RESUME_MODE) && (
        <StressStartConfirmDialog
          open={newDialogOpen}
          onClose={() => setNewDialogOpen(false)}
          onSubmit={() => {
            setNewDialogOpen(false);
            handleStressorProcessing();
          }}
          title={
            state.mode === STRESSOR_CHECK_IN_MODE
              ? "Start Stressor Confirmation"
              : "Resume Stressor Confirmation"
          }
          content={
            state.mode === STRESSOR_CHECK_IN_MODE
              ? "Are you sure that you want to start stressor?"
              : "Are you sure that you want to resume stressor?"
          }
        />
      )}

      <ConfirmationDialog
        id="stressor-start-confirmation"
        keepMounted
        open={confirmationDialogStartStressorOpen}
        title={
          state.mode === STRESSOR_CHECK_OUT_MODE
            ? "End Stressor Confirmation"
            : "Pause Stressor Confirmation"
        }
        content={
          state.mode === STRESSOR_CHECK_OUT_MODE
            ? "Are you sure that you want to end stressor?"
            : "Are you sure that you want to pause stressor?"
        }
        onCancel={() => setConfirmationDialogStartStressorOpen(false)}
        onSubmit={handleStressorProcessing}
      />
    </Grid>
  );
};
